<script lang='ts'>
import { reactive, defineComponent, computed, ref } from '@vue/composition-api';
import { useAccountStore, UpdateProfile, CancelPendingEmailChangeRequest } from '@/store/global/account';
import { useCurrentUserModule } from '@/store/global/currentUser';
import { useForm, useAsyncValidation } from '@/_core/utils/forms';
import { required, isEmail } from '@/_core/utils/forms/validators';
import { queryExecutor } from '@/_core/cqrs';
import { EmailAddressIsUniqueQuery } from '@/api/account/queries';
import { snackbar } from '@/_core/app-components/use';

import { confirm } from '@/_core/app-components/use';
import { trackingService } from '@/_core/tracking/trackingService';
import { TrackedPage } from '../../../_core/tracking/trackedpages';

const AccountForm = defineComponent({
    name: 'AccountForm',

    setup(props, ctx) {
        const accountStore = useAccountStore();
        const currentUserStore = useCurrentUserModule();

        const state = reactive({
            name: accountStore.state.name,
            email: accountStore.state.email,
            newsLetter: accountStore.state.subscribeToNewsletter,

            isNameDisabled: computed(() => currentUserStore.state.ssoEnabledForUser),
            isEmailDisabled: computed(() => currentUserStore.state.ssoEnabledForUser || currentUserStore.state.userIsDomainControlled),

            hasPendingEmailAddressChange: computed(() => accountStore.state.hasPendingEmailAddressChange),
            requestedNewEmailAddress: computed(() => accountStore.state.requestedNewEmailAddress),
        });

        const formElement = ref('');
        const form = useForm(formElement, { required, isEmail });
        const validateEmail = async (value: string): Promise<string | boolean> => {
            if (!value || value.length == 0) {
                return true;
            }
            const res = await queryExecutor.executeQuery(new EmailAddressIsUniqueQuery(accountStore.state.email, value));
            if (res == false) {
                return 'Already in use. Please select an another email address!';
            }

            return true;
        };
        const emailErrors = useAsyncValidation(() => state.email, validateEmail);

        const close = () => {
            ctx.emit('close');
        };

        const save = async () => {
            form.isSaving = true;

            await accountStore.dispatch(new UpdateProfile(state.name, state.email, state.newsLetter));
            snackbar.showSuccess('Your profile sucessfully updated!');

            form.isSaving = false;
            close();
        };

        const onSave = () => {
            form.saveForm(async () => {
                if (
                    state.name != accountStore.state.name ||
                    state.email != accountStore.state.email ||
                    state.newsLetter != accountStore.state.subscribeToNewsletter
                ) {
                    if (accountStore.state.email != state.email) {
                        confirm.show({
                            title: 'Are you sure?',
                            message:
                                'Your email address is used as your account sign-on. If you change it, you need to respond to a confirmation email sent to the new address before the change is applied.',
                            acceptBtnText: 'Confirm',
                            cancelBtnText: 'Cancel',
                            acceptFn: save,
                            width: 800,
                        });
                    } else {
                        await save();
                    }
                } else {
                    close();
                }
            });
        };

        const cancelPendingEmailChangeRequest = async () => {
            form.isSaving = true;
            await accountStore.dispatch(new CancelPendingEmailChangeRequest());
            form.isSaving = false;
            snackbar.showSuccess('Your email address change request has been cancelled.');
        };

        const openConsentManager = () => {
            if (consentManager) {
                consentManager.openConsentManager();
                trackingService.TrackSubpageView(TrackedPage.DataCollectionPreferences);
            }
        };

        return {
            state,
            close,
            onSave,
            cancelPendingEmailChangeRequest,
            form,
            formElement,
            emailErrors,
            openConsentManager,
        };
    },
});

export default AccountForm;
</script>

<template>
  <v-card flat class="pt-2">
    <v-progress-linear
      :active="form.isSaving"
      indeterminate        
      color="primary lighten-2"
      class="mt-1"
    />

    <v-card-text>
      <AppWarning v-if="state.isNameDisabled && state.isEmailDisabled" :dismissable="false">
        Hint: You can't change your account details because your account is under domain control and managed by your company.
      </AppWarning>

      <AppWarning v-else-if="state.isEmailDisabled" :dismissable="false">
        You can't change your email address because your account is under domain control and managed by your company.
      </AppWarning>

      <AppWarning v-else-if="state.hasPendingEmailAddressChange" :dismissable="false">
        An email address change request has been sent to <span class="font-weight-bold">{{ state.requestedNewEmailAddress }}</span>. To complete the request, click the link in the confirmation email. 
        You may  cancel   this request if needed. 
        <v-btn
          depressed 
          color="secondaryButton"
          class="primary--text"
          small
          :loading="form.isSaving"
          :disabled="form.isSaving"
          @click="cancelPendingEmailChangeRequest"
        >
          Cancel
        </v-btn>
      </AppWarning>


      <v-form
        ref="formElement"
        v-model="form.valid"
        :lazy-validation="lazy"
        @keydown.native.enter.prevent="onSave"
        @keydown.native.esc.stop="esc"
      >
        <v-text-field
          v-model="state.name" 
          :rules="[form.rules.required()]"
          :disabled="state.isNameDisabled || form.isSaving"
          label="Your name"
        />

        <v-text-field
          v-model="state.email"
          :rules="[form.rules.required(), form.rules.isEmail()]"
          :error-messages="emailErrors"
          :disabled="state.isEmailDisabled || form.isSaving"
          class="mt-3"
          label="Your email"
          hint="Your email address is used as your account sign-on. If you change it, you need to respond to a confirmation email sent to the new address before the change is applied."
        />
        <v-checkbox
          v-model="state.newsLetter"
          :disabled="form.isSaving"
          label="Subscribe to newsletter"
          class="mt-2"
        />
      </v-form>
    </v-card-text>
 
    <v-card-actions class="pt-0">
      <v-btn
        color="primary"
        depressed
        :loading="form.isSaving"
        :disabled="!form.valid"
        @click="onSave"
      >
        Save
      </v-btn>
      <v-btn
        depressed 
        color="secondaryButton"
        class="primary--text"
        @click="close"
      >
        Cancel
      </v-btn>
      
      <v-spacer />
      <v-btn
        class="mx-0"
        color="secondaryButton"
        depressed
        @click="openConsentManager"
      >
        Data collection preferences
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<style lang='sass' scoped></style>